import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Package2,
  ArrowUp,
  ArrowDown,
  RotateCcw
} from 'lucide-react'
// import { stockTransactionAPI, stockAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import toast from 'react-hot-toast'

function StockTransactions() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStock, setSelectedStock] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingTransaction, setEditingTransaction] = useState(null)

  const queryClient = useQueryClient()

  // Fetch transactions
  const { data: transactionsData, isLoading } = useQuery(
    ['stock-transactions', { page: currentPage, search: searchTerm, stockId: selectedStock, type: selectedType }],
    () => stockTransactionAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      stockId: selectedStock,
      type: selectedType,
    })
  )

  // Fetch stocks for filter
  const { data: stocksData } = useQuery('stocks', () => stockAPI.getAll({ limit: 100 }))

  // Create transaction mutation
  const createTransactionMutation = useMutation(stockTransactionAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('stock-transactions')
      queryClient.invalidateQueries('stocks')
      setShowModal(false)
      toast.success('Transaction created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create transaction')
    },
  })

  // Update transaction mutation
  const updateTransactionMutation = useMutation(
    ({ id, data }) => stockTransactionAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('stock-transactions')
        queryClient.invalidateQueries('stocks')
        setShowModal(false)
        setEditingTransaction(null)
        toast.success('Transaction updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update transaction')
      },
    }
  )

  // Delete transaction mutation
  const deleteTransactionMutation = useMutation(stockTransactionAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('stock-transactions')
      queryClient.invalidateQueries('stocks')
      toast.success('Transaction deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete transaction')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingTransaction) {
      updateTransactionMutation.mutate({ id: editingTransaction.id, data })
    } else {
      createTransactionMutation.mutate(data)
    }
  }

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction)
    reset({
      stockId: transaction.stockId,
      quantity: transaction.quantity,
      type: transaction.type,
      issuedBy: transaction.issuedBy,
      receivedBy: transaction.receivedBy,
      transactionDate: transaction.transactionDate,
      remarks: transaction.remarks,
    })
    setShowModal(true)
  }

  const handleDelete = (transaction) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransactionMutation.mutate(transaction.id)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingTransaction(null)
    reset()
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'Issue':
        return <ArrowUp className="h-4 w-4 text-red-600" />
      case 'Receive':
        return <ArrowDown className="h-4 w-4 text-green-600" />
      case 'Adjustment':
        return <RotateCcw className="h-4 w-4 text-blue-600" />
      default:
        return <Package2 className="h-4 w-4 text-gray-600" />
    }
  }

  const getTransactionColor = (type) => {
    switch (type) {
      case 'Issue':
        return 'badge-danger'
      case 'Receive':
        return 'badge-success'
      case 'Adjustment':
        return 'badge-secondary'
      default:
        return 'badge-secondary'
    }
  }

  const transactions = transactionsData?.data?.transactions || []
  const stocks = stocksData?.data?.stocks || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Stock Transactions</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage stock issue, receive, and adjustment transactions
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Transaction
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Stock Item</label>
              <select
                value={selectedStock}
                onChange={(e) => setSelectedStock(e.target.value)}
                className="input"
              >
                <option value="">All Items</option>
                {stocks.map((stock) => (
                  <option key={stock.id} value={stock.id}>
                    {stock.itemName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Transaction Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="input"
              >
                <option value="">All Types</option>
                <option value="Issue">Issue</option>
                <option value="Receive">Receive</option>
                <option value="Adjustment">Adjustment</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStock('')
                  setSelectedType('')
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Stock Item</th>
                    <th className="table-head">Type</th>
                    <th className="table-head">Quantity</th>
                    <th className="table-head">Issued By</th>
                    <th className="table-head">Received By</th>
                    <th className="table-head">Date</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <Package2 className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {transaction.stock?.itemName}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {transaction.stock?.category}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          {getTransactionIcon(transaction.type)}
                          <span className={`ml-1 badge ${getTransactionColor(transaction.type)}`}>
                            {transaction.type}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {transaction.quantity}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {transaction.issuer?.firstName} {transaction.issuer?.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {transaction.receiver?.firstName} {transaction.receiver?.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {new Date(transaction.transactionDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(transaction)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Transaction Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-2xl">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">Stock Item</label>
                  <select
                    {...register('stockId', { required: 'Stock item is required' })}
                    className="input"
                  >
                    <option value="">Select a stock item</option>
                    {stocks.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.itemName} - Available: {stock.availableQuantity}
                      </option>
                    ))}
                  </select>
                  {errors.stockId && (
                    <p className="form-error">{errors.stockId.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Transaction Type</label>
                    <select
                      {...register('type', { required: 'Transaction type is required' })}
                      className="input"
                    >
                      <option value="">Select type</option>
                      <option value="Issue">Issue</option>
                      <option value="Receive">Receive</option>
                      <option value="Adjustment">Adjustment</option>
                    </select>
                    {errors.type && (
                      <p className="form-error">{errors.type.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Quantity</label>
                    <input
                      {...register('quantity', { required: 'Quantity is required' })}
                      type="number"
                      step="0.01"
                      className="input"
                      placeholder="Enter quantity"
                    />
                    {errors.quantity && (
                      <p className="form-error">{errors.quantity.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Issued By (User ID)</label>
                    <input
                      {...register('issuedBy')}
                      type="number"
                      className="input"
                      placeholder="Enter user ID"
                    />
                  </div>
                  <div>
                    <label className="label">Received By (User ID)</label>
                    <input
                      {...register('receivedBy')}
                      type="number"
                      className="input"
                      placeholder="Enter user ID"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Transaction Date</label>
                  <input
                    {...register('transactionDate')}
                    type="datetime-local"
                    className="input"
                  />
                </div>

                <div>
                  <label className="label">Remarks</label>
                  <textarea
                    {...register('remarks')}
                    className="input"
                    rows={3}
                    placeholder="Enter transaction remarks"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createTransactionMutation.isLoading || updateTransactionMutation.isLoading}
                    className="btn-primary"
                  >
                    {createTransactionMutation.isLoading || updateTransactionMutation.isLoading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : null}
                    {editingTransaction ? 'Update Transaction' : 'Create Transaction'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StockTransactions
